import { Box, Typography } from '@mui/material';

import { SHRED_COLORS, SHRED_COPY, SHRED_CREW_MILESTONES, SHRED_FONTS } from '../constants';
import { formatCompact, getCrewProgress, pluralize } from '../helpers';
import { CountUp, Panel, SectionTitle } from './shared';

const CrewMeter = ({ crew, reward, reduced }) => {
  const progress = getCrewProgress(crew);
  const hasGoal = crew.goalWork > 0;
  const maxDay = Math.max(1, ...crew.byDay.map((d) => d.totalWork));
  return (
    <Box component="section" aria-labelledby="shred-crew-title">
      <SectionTitle
        kicker="Everyone together"
        title={SHRED_COPY.CREW_TITLE}
        id="shred-crew-title"
        hint={reward ? `Crew reward: ${reward}` : 'One shared work goal for the whole crew.'}
      />
      <Panel sx={{ p: { xs: 2, sm: 3 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: 1 }}>
          <Typography sx={{ fontFamily: SHRED_FONTS.numeric, fontSize: { xs: 26, sm: 34 }, color: SHRED_COLORS.gold, fontVariantNumeric: 'tabular-nums' }}>
            <CountUp value={crew.totalWork} format={(v) => formatCompact(v)} reduced={reduced} />
            <Box component="span" sx={{ color: SHRED_COLORS.muted, fontSize: '0.5em' }}>
              {hasGoal ? ` / ${formatCompact(crew.goalWork)} work` : ' work'}
            </Box>
          </Typography>
          {hasGoal && (
            <Typography sx={{ fontFamily: SHRED_FONTS.display, fontWeight: 800, fontSize: 34, color: progress.pct >= 100 ? SHRED_COLORS.positive : SHRED_COLORS.text }}>
              <CountUp value={progress.displayPct} format={(v) => `${Math.round(v)}%`} reduced={reduced} />
            </Typography>
          )}
        </Box>

        {hasGoal && (
          <Box sx={{ position: 'relative', mt: 3, mb: 3.5 }}>
            <Box
              role="progressbar"
              aria-label="Crew goal progress"
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={Math.min(100, progress.displayPct)}
              sx={{ height: 22, borderRadius: '11px', background: 'rgba(255,255,255,0.09)', overflow: 'hidden', position: 'relative' }}
            >
              <Box
                className="shred-motion"
                sx={{
                  height: '100%',
                  width: `${progress.pct}%`,
                  borderRadius: '11px',
                  background: `repeating-linear-gradient(115deg, rgba(255,255,255,0.18) 0 10px, transparent 10px 22px), ${SHRED_COLORS.heat}`,
                  backgroundSize: '44px 100%, 100% 100%',
                  boxShadow: '0 0 18px rgba(255,90,31,0.7)',
                  transition: reduced ? 'none' : 'width 1200ms cubic-bezier(0.2,0.8,0.2,1)',
                  animation: 'shredHeatStripe 1.6s linear infinite',
                }}
              />
            </Box>
            {progress.pct > 2 && (
              <Box
                aria-hidden="true"
                className="shred-motion"
                sx={{
                  position: 'absolute',
                  top: -18,
                  left: `${progress.pct}%`,
                  transform: 'translateX(-50%)',
                  fontSize: 26,
                  transition: reduced ? 'none' : 'left 1200ms cubic-bezier(0.2,0.8,0.2,1)',
                  animation: 'shredFlame 0.9s ease-in-out infinite',
                  transformOrigin: '50% 100%',
                }}
              >
                🔥
              </Box>
            )}
            {SHRED_CREW_MILESTONES.map((milestone) => {
              const reached = progress.reached.includes(milestone);
              return (
                <Box
                  key={milestone}
                  sx={{ position: 'absolute', top: -4, left: `${milestone}%`, transform: 'translateX(-100%)', display: milestone === 100 ? 'block' : 'block' }}
                >
                  <Box sx={{ width: 2, height: 30, ml: 'auto', background: reached ? SHRED_COLORS.gold : 'rgba(255,255,255,0.3)' }} />
                  <Typography
                    sx={{
                      position: 'absolute',
                      top: 32,
                      right: -14,
                      fontFamily: SHRED_FONTS.numeric,
                      fontSize: 11,
                      color: reached ? SHRED_COLORS.gold : SHRED_COLORS.muted,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {reached ? '✓ ' : ''}{milestone}%
                  </Typography>
                </Box>
              );
            })}
          </Box>
        )}

        <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', fontFamily: SHRED_FONTS.body, fontSize: 14, color: SHRED_COLORS.muted }}>
          <span><b style={{ color: SHRED_COLORS.text }}>{crew.participants}</b> {crew.participants === 1 ? 'member' : 'members'} in</span>
          <span><b style={{ color: SHRED_COLORS.text }}>{crew.sessions}</b> {pluralize(crew.sessions, 'session').replace(/^\d+ /, '')}</span>
        </Box>

        {crew.byDay.length > 0 && (
          <Box aria-hidden="true" sx={{ mt: 2, display: 'flex', alignItems: 'flex-end', gap: '4px', height: 56 }}>
            {crew.byDay.map((day) => (
              <Box
                key={day.day}
                title={`Day ${day.day}: ${formatCompact(day.totalWork)}`}
                sx={{
                  flex: 1,
                  height: `${Math.max(6, (day.totalWork / maxDay) * 100)}%`,
                  borderRadius: '3px 3px 0 0',
                  background: SHRED_COLORS.heat,
                  opacity: 0.85,
                  transition: reduced ? 'none' : 'height 700ms ease',
                }}
              />
            ))}
          </Box>
        )}
      </Panel>
    </Box>
  );
};

export default CrewMeter;
